/**
 * Store for managing the list of projects (agent projects).
 * Handles fetching, caching, and CRUD operations on the project list.
 */
import { create } from 'zustand';
import { HttpAgentAdapter } from '../services/agent/HttpAgentAdapter';
import type { BusinessCaseSummary } from '../services/agent/AgentService';
import { toAppError, AppError } from '../types/api';
import Logger from '../utils/logger';

const logger = Logger.create('projectListStore');

// Singleton service instance
const agentService = new HttpAgentAdapter();

interface ProjectListState {
  // Data
  projects: BusinessCaseSummary[];

  // Loading states
  isLoading: boolean;
  isDeleting: boolean;

  // Errors
  error: AppError | null;

  // Actions
  fetchProjects: () => Promise<void>;
  deleteProject: (projectId: string) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  projects: [],
  isLoading: false,
  isDeleting: false,
  error: null,
};

export const useProjectListStore = create<ProjectListState>((set, get) => ({
  ...initialState,

  fetchProjects: async () => {
    // Skip if already loading
    if (get().isLoading) {
      logger.debug('fetchProjects: Already loading, skipping');
      return;
    }

    set({ isLoading: true, error: null });

    try {
      logger.debug('Fetching user projects');
      const projects = await agentService.listCases();
      set({ projects, isLoading: false });
      logger.debug(`Fetched ${projects.length} projects`);
    } catch (err) {
      const appError = toAppError(err, 'api');
      logger.error('Error fetching projects:', appError);
      set({ isLoading: false, error: appError });
    }
  },

  deleteProject: async (projectId: string) => {
    set({ isDeleting: true, error: null });

    try {
      logger.debug(`Deleting project: ${projectId}`);
      await agentService.deleteCase(projectId);

      // Remove from local state
      set((state) => ({
        projects: state.projects.filter((p) => p.case_id !== projectId),
        isDeleting: false,
      }));

      logger.debug(`Project ${projectId} deleted successfully`);
      return true;
    } catch (err) {
      const appError = toAppError(err, 'api');
      logger.error('Error deleting project:', appError);
      set({ isDeleting: false, error: appError });
      return false;
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}));

// Selector hooks for optimized subscriptions
export const useProjects = () => useProjectListStore((state) => state.projects);
export const useProjectListLoading = () =>
  useProjectListStore((state) => state.isLoading);
export const useProjectListError = () =>
  useProjectListStore((state) => state.error);
